/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { TalerError, TranslatedString } from "@gnu-taler/taler-util";
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";
import { useState } from "preact/hooks";

export type VerificationStep = "details" | "code" | "complete";

export function VerificationCard({
  title,
  description,
  step,
  children,
}: {
  title: TranslatedString;
  description?: ComponentChildren;
  step?: VerificationStep;
  children?: ComponentChildren;
}): VNode {
  return (
    <section class="challenger-card w-full max-w-xl rounded-2xl border border-outlineVariant bg-background px-5 py-7 text-onBackground shadow-sm dark:border-darkSecondaryContainer dark:bg-darkBackground dark:text-darkOnBackground sm:px-9 sm:py-10">
      {step ? <VerificationProgress current={step} /> : undefined}
      <div class="text-center">
        <h1 class="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {description ? (
          <div class="mx-auto mt-3 max-w-lg text-base leading-7 text-secondary dark:text-darkSecondary">
            {description}
          </div>
        ) : undefined}
      </div>
      <div class="mt-7">{children}</div>
    </section>
  );
}

function VerificationProgress({
  current,
}: {
  current: VerificationStep;
}): VNode {
  const { i18n } = useTranslationContext();
  const active = current === "details" ? 1 : current === "code" ? 2 : 3;
  // Translators: Short progress-step label for entering an email address,
  // phone number, or postal address; this does not mean a contact person.
  const contactStep = i18n.str`Contact`;
  // Translators: Short progress-step label for entering the verification code.
  const codeStep = i18n.str`Code`;
  // Translators: Short progress-step label for the finished verification.
  const completeStep = i18n.str`Complete`;
  const steps = [contactStep, codeStep, completeStep];
  // Translators: Accessible label for the three-step contact, code, and
  // completion progress indicator.
  const progressLabel = i18n.str`Verification progress`;
  return (
    <ol class="mb-7 grid grid-cols-3 gap-2" aria-label={progressLabel}>
      {steps.map((label, index) => {
        const number = index + 1;
        const selected = number === active;
        const done = number < active;
        return (
          <li
            key={label}
            class={`border-t-4 pt-2 text-center text-xs font-semibold sm:text-sm ${
              selected || done
                ? "border-primary text-primary dark:border-darkPrimary dark:text-darkPrimary"
                : "border-outlineVariant text-secondary dark:border-outline dark:text-darkSecondary"
            }`}
            aria-current={selected ? "step" : undefined}
          >
            {label}
          </li>
        );
      })}
    </ol>
  );
}

export function LoadingState({
  title,
  description,
}: {
  title: TranslatedString;
  description?: TranslatedString;
}): VNode {
  return (
    <VerificationCard title={title} description={description}>
      <div class="flex justify-center" role="status" aria-live="polite">
        <Spinner />
      </div>
    </VerificationCard>
  );
}

export function Spinner(): VNode {
  return (
    <svg
      class="h-7 w-7 animate-spin text-primary dark:text-darkPrimary"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        stroke-width="3"
      />
      <path
        class="opacity-90"
        fill="currentColor"
        d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3z"
      />
    </svg>
  );
}

export function ActionButton({
  children,
  busyLabel,
  running = false,
  disabled = false,
  onClick,
  submit = false,
}: {
  children: ComponentChildren;
  busyLabel: TranslatedString;
  running?: boolean;
  disabled?: boolean;
  onClick?: () => void | Promise<void>;
  submit?: boolean;
}): VNode {
  return (
    <button
      type={submit ? "submit" : "button"}
      class="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-onPrimary shadow-sm hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-outlineVariant disabled:text-secondary disabled:opacity-60 dark:bg-darkPrimary dark:text-darkOnPrimary dark:focus-visible:outline-darkPrimary dark:disabled:bg-darkSecondaryContainer dark:disabled:text-darkSecondary"
      disabled={disabled || running || (!submit && !onClick)}
      aria-busy={running}
      onClick={onClick}
    >
      {running ? <Spinner /> : undefined}
      <span>{running ? busyLabel : children}</span>
    </button>
  );
}

export function SecondaryAction({
  href,
  children,
}: {
  href: string;
  children: ComponentChildren;
}): VNode {
  return (
    <a
      href={href}
      class="flex min-h-11 w-full items-center justify-center rounded-lg border border-outline bg-background px-4 py-2.5 text-center text-sm font-semibold text-onBackground hover:bg-secondaryContainer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-darkSecondary dark:bg-darkBackground dark:text-darkOnBackground dark:hover:bg-darkSecondaryContainer dark:focus-visible:outline-darkPrimary"
    >
      {children}
    </a>
  );
}

export function SecondaryButton({
  children,
  busyLabel,
  running = false,
  disabled = false,
  onClick,
}: {
  children: ComponentChildren;
  busyLabel: TranslatedString;
  running?: boolean;
  disabled?: boolean;
  onClick?: () => void | Promise<void>;
}): VNode {
  return (
    <button
      type="button"
      class="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-outline bg-background px-4 py-2.5 text-center text-sm font-semibold text-onBackground hover:bg-secondaryContainer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:border-outlineVariant disabled:text-secondary disabled:opacity-60 dark:border-darkSecondary dark:bg-darkBackground dark:text-darkOnBackground dark:hover:bg-darkSecondaryContainer dark:focus-visible:outline-darkPrimary dark:disabled:border-darkSecondaryContainer dark:disabled:text-darkSecondary"
      disabled={disabled || running || !onClick}
      aria-busy={running}
      onClick={onClick}
    >
      {running ? <Spinner /> : undefined}
      <span>{running ? busyLabel : children}</span>
    </button>
  );
}

export function TextActionLink({
  href,
  children,
}: {
  href: string;
  children: ComponentChildren;
}): VNode {
  return (
    <a
      href={href}
      class="inline-flex min-h-11 items-center rounded-md px-1 text-sm font-semibold text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:text-darkPrimary dark:decoration-darkPrimary/40 dark:hover:decoration-darkPrimary dark:focus-visible:outline-darkPrimary"
    >
      {children}
    </a>
  );
}

export function PrimaryActionLink({
  href,
  children,
}: {
  href: string;
  children: ComponentChildren;
}): VNode {
  return (
    <a
      href={href}
      class="flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold text-onPrimary shadow-sm hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-darkPrimary dark:text-darkOnPrimary dark:focus-visible:outline-darkPrimary"
    >
      {children}
    </a>
  );
}

export type NoticeTone = "info" | "error" | "warning" | "success";

export function InlineNotice({
  title,
  tone = "info",
  children,
}: {
  title: TranslatedString;
  tone?: NoticeTone;
  children?: ComponentChildren;
}): VNode {
  const toneClass = {
    info: "border-primary bg-primaryContainer text-onPrimaryContainer dark:border-darkPrimary dark:bg-darkPrimaryContainer dark:text-darkOnPrimaryContainer",
    error:
      "border-error bg-errorContainer text-onErrorContainer dark:border-darkError dark:bg-darkErrorContainer dark:text-darkOnErrorContainer",
    warning:
      "border-warning bg-warningContainer text-onWarningContainer dark:border-darkWarning dark:bg-darkWarningContainer dark:text-darkOnWarningContainer",
    success:
      "border-success bg-successContainer text-onSuccessContainer dark:border-darkSuccess dark:bg-darkSuccessContainer dark:text-darkOnSuccessContainer",
  }[tone];
  return (
    <div
      class={`rounded-lg border-l-4 p-4 text-sm ${toneClass}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <p class="font-bold">{title}</p>
      {children ? <div class="mt-1 leading-6">{children}</div> : undefined}
    </div>
  );
}

export function TechnicalDetails({
  children,
  copyText,
}: {
  children: ComponentChildren;
  copyText?: string;
}): VNode {
  const { i18n } = useTranslationContext();
  const [copied, setCopied] = useState(false);
  // Translators: Label on an expandable section containing diagnostic
  // information intended mainly for support and developers.
  const summaryLabel = i18n.str`Technical details`;
  // Translators: Button that copies diagnostic error details to the clipboard.
  const copyLabel = i18n.str`Copy details`;
  // Translators: Brief confirmation replacing “Copy details” after the
  // diagnostic information has been copied to the clipboard.
  const copiedLabel = i18n.str`Copied`;
  return (
    <details class="mt-4 text-sm text-secondary dark:text-darkSecondary">
      <summary class="cursor-pointer font-semibold">{summaryLabel}</summary>
      <div class="mt-2 break-words rounded-lg border border-outlineVariant p-3 font-mono text-xs dark:border-darkSecondaryContainer">
        <div class="whitespace-pre-wrap">{children}</div>
        {copyText ? (
          <button
            type="button"
            class="mt-3 min-h-9 rounded-md border border-outline px-3 py-1.5 font-sans text-xs font-semibold text-onBackground hover:bg-secondaryContainer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-darkSecondary dark:text-darkOnBackground dark:hover:bg-darkSecondaryContainer dark:focus-visible:outline-darkPrimary"
            onClick={async () => {
              try {
                await copyToClipboard(copyText);
                setCopied(true);
              } catch {
                setCopied(false);
              }
            }}
          >
            <span aria-live="polite">{copied ? copiedLabel : copyLabel}</span>
          </button>
        ) : undefined}
      </div>
    </details>
  );
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("clipboard copy command failed");
    }
  } finally {
    input.remove();
  }
}

export function formatHttpErrorDetails(failure: {
  case: number;
  detail?: unknown;
}): string {
  return JSON.stringify(
    {
      httpStatus: failure.case,
      error: failure.detail ?? null,
    },
    undefined,
    2,
  );
}

export function formatErrorDetails(error: unknown): string {
  if (error instanceof TalerError) {
    return JSON.stringify(error.errorDetail, undefined, 2);
  }
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  return String(error);
}
